import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { OwnerWorkPendingError, useAuth } from '@/hooks/useAuth';
import { useNavLayer } from '@/hooks/useNavLayer';
import { getAuthEpoch } from '@/lib/keepsake/epochs';
import { isCoreReadOnly } from '@/lib/core-cutover';
import {
  notesListShown,
  notesListShownVersion,
  readSignOutCauses,
  subscribeNotesListShown,
  type SignOutCauses,
} from '@/lib/sign-out-causes';
import AuthModal from './AuthModal';
import styles from './AuthSection.module.css';

export default function AuthSection() {
  const { t } = useTranslation();
  const { user, loading, signOut } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [pendingWork, setPendingWork] = useState(false);
  // What a refused sign-out can observe (read-only; sign-out itself and every
  // owner check are unchanged). Bound to the session it was read for.
  const [causes, setCauses] = useState<(SignOutCauses & { ownerId: string; authEpoch: number }) | null>(null);
  const causeSeq = useRef(0);
  // Re-render when the notes list appears or goes, so "above" stays true.
  useSyncExternalStore(subscribeNotesListShown, notesListShownVersion);

  // NavStack layer for the sign-in modal (AB, rule #78). Hook declared BEFORE
  // the `loading` early return so its order is unconditional; `showModal` is
  // false at mount (and while loading) so it registers closed (StrictMode-safe).
  const zAuth = useNavLayer('settings-auth', showModal, () => setShowModal(false));

  // Host-owned, state-gated Escape (AB). AuthModal has no internal Escape
  // handler; this mirrors A1's MailCardSheet pattern for `mailcard-auth` so the
  // shared AuthModal body stays untouched. setShowModal(false) routes through
  // the useNavLayer teardown to requestClose, converging with X / backdrop /
  // browser-back on the single reconciler path.
  useEffect(() => {
    if (!showModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showModal]);

  if (loading) return null;

  async function handleSignOut(force = false) {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut(force ? { force: true } : undefined);
    } catch (error) {
      if (error instanceof OwnerWorkPendingError) {
        setPendingWork(true);
        const ownerId = user?.id;
        const authEpoch = getAuthEpoch();
        const seq = ++causeSeq.current;
        if (ownerId) {
          try {
            const read = await readSignOutCauses(ownerId);
            if (seq === causeSeq.current && getAuthEpoch() === authEpoch) {
              setCauses({ ...read, ownerId, authEpoch });
            }
          } catch {
            if (seq === causeSeq.current) setCauses(null);
          }
        }
      }
      setSigningOut(false);
    }
  }

  const shownCauses = causes && user?.id === causes.ownerId && getAuthEpoch() === causes.authEpoch
    ? causes
    : null;
  const causeLines = (read: SignOutCauses & { ownerId: string; authEpoch: number }) => read.causes.map((cause) => {
    switch (cause) {
      case 'recording-failed':
        return read.tryAgainShown
          ? `${t('auth.signOutBlocked.recordingFailed')} ${t('auth.signOutBlocked.recordingTryAgain')}`
          : t('auth.signOutBlocked.recordingFailed');
      case 'recording-unresolved':
        return t('auth.signOutBlocked.recordingUnresolved');
      case 'recording-active':
        return t('auth.signOutBlocked.recordingActive');
      case 'home-words':
        return t('auth.signOutBlocked.homeWords');
      case 'interrupted-words':
        return t('auth.signOutBlocked.interruptedWords');
      case 'notes':
        return notesListShown(read.ownerId, read.authEpoch)
          ? `${t('auth.signOutBlocked.notes')} ${t('auth.signOutBlocked.notesAbove')}`
          : t('auth.signOutBlocked.notes');
      case 'attempts':
        // The unfinished-changes card below renders whenever writes are open.
        return !isCoreReadOnly()
          ? `${t('auth.signOutBlocked.attempts')} ${t('auth.signOutBlocked.attemptsBelow')}`
          : t('auth.signOutBlocked.attempts');
      case 'recordings-kept':
        return t('auth.signOutBlocked.recordingsKept');
      case 'unsent':
        return read.offline
          ? `${t('auth.signOutBlocked.unsent')} ${t('auth.signOutBlocked.needsConnection')}`
          : t('auth.signOutBlocked.unsent');
      default:
        return t('auth.signOutBlocked.other');
    }
  });

  return (
    <>
      {user ? (
        <div className={styles.account}>
          <p className={styles.email}>{user.email}</p>
          {pendingWork ? (
            <>
              <p className={styles.description}>{t('auth.signOutBlocked.intro')}</p>
              {shownCauses && (
                <ul className={styles.causes}>
                  {causeLines(shownCauses).map((line, index) => (
                    <li key={`${shownCauses.causes[index]}`} className={styles.description}>{line}</li>
                  ))}
                </ul>
              )}
              {shownCauses && (
                <p className={styles.description}>
                  {shownCauses.inPageRecording
                    ? t('auth.signOutBlocked.forceRecording')
                    : t('auth.signOutBlocked.forceKeeps')}
                </p>
              )}
              <div className={styles.signOutActions}>
                <button
                  type="button"
                  className={styles.loginBtn}
                  disabled={signingOut}
                  onClick={() => handleSignOut()}
                >
                  {t('accountLoad.retry')}
                </button>
                <button
                  type="button"
                  className={styles.logoutBtn}
                  disabled={signingOut}
                  onClick={() => handleSignOut(true)}
                >
                  {signingOut ? t('accountLoad.signingOut') : t('accountLoad.signOut')}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className={styles.logoutBtn}
              disabled={signingOut}
              onClick={() => handleSignOut()}
            >
              {t('auth.logout')}
            </button>
          )}
        </div>
      ) : (
        <div className={styles.account}>
          <p className={styles.description}>{t('auth.settingsDesc')}</p>
          <button
            type="button"
            className={styles.loginBtn}
            onClick={() => setShowModal(true)}
          >
            {t('auth.login')}
          </button>
        </div>
      )}

      {showModal && (
        <AuthModal onClose={() => setShowModal(false)} zIndex={zAuth} />
      )}
    </>
  );
}
